import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
interface ParticleFieldProps {
  type: 'fire' | 'earth' | 'air' | 'water' | 'electric' | 'smoke' | 'light' | 'cosmic';
  color?: string;
  density?: number;
  className?: string;
}
// Detect mobile safely (works during initial render without crashing)
function detectIsMobile(): boolean {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 768 || /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent || '');
}
// Detect reduced-motion preference
function detectReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}
export function ParticleField({
  type,
  color,
  density = 30,
  className = ''
}: ParticleFieldProps) {
  // Defer mobile detection to client-side to prevent any SSR/hydration issues
  const [isReady, setIsReady] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    setIsMobile(detectIsMobile());
    setReducedMotion(detectReducedMotion());
    setIsReady(true);
  }, []);
  const particles = useMemo(() => {
    if (!isReady) return [];
    if (reducedMotion) return [];
    // Aggressive mobile reduction: ~30% of desktop density, hard cap at 18 particles
    const particleCount = isMobile ? Math.min(Math.max(Math.floor(density * 0.3), 4), 18) : Math.min(density, 60);
    return Array.from({
      length: particleCount
    }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      size: Math.random() * 4 + 1,
      duration: Math.random() * 10 + 15,
      delay: Math.random() * 5,
      xJitter1: Math.random() * 30 - 15,
      xJitter2: Math.random() * 30 - 15,
      xJitter3: Math.random() * 60 - 30,
      baseX: Math.random() * 100 - 50,
      sinSeed: Math.random() * Math.PI * 2
    }));
  }, [density, isReady, isMobile, reducedMotion]);
  const getParticleStyle = (t: string) => {
    switch (t) {
      case 'fire':
        return {
          backgroundColor: color || '#FF6B35',
          filter: 'blur(2px)'
        };
      case 'earth':
        return {
          backgroundColor: color || '#D4AF37',
          filter: 'blur(1px)'
        };
      case 'air':
        return {
          backgroundColor: color || '#C0C0C0',
          filter: 'blur(3px)',
          opacity: 0.4
        };
      case 'water':
        return {
          backgroundColor: color || '#40E0D0',
          filter: 'blur(2px)',
          opacity: 0.6
        };
      case 'electric':
        return {
          backgroundColor: color || '#00BFFF',
          filter: 'blur(1px)'
        };
      case 'smoke':
        return {
          backgroundColor: color || '#8B008B',
          filter: 'blur(4px)',
          opacity: 0.3
        };
      case 'light':
        return {
          backgroundColor: color || '#FFD700',
          filter: 'blur(2px)'
        };
      case 'cosmic':
      default:
        return {
          backgroundColor: color || '#C9A84C',
          filter: 'blur(2px)',
          opacity: 0.5
        };
    }
  };
  const getAnimation = (t: string, p: (typeof particles)[number]) => {
    const baseY = ['0vh', '-100vh'];
    switch (t) {
      case 'fire':
        return {
          y: baseY,
          x: [0, p.xJitter1, p.xJitter2],
          opacity: [0, 1, 1, 0],
          scale: [1, 1.5, 0.5]
        };
      case 'electric':
        return {
          y: baseY,
          x: [0, p.xJitter3, p.xJitter3, 0],
          opacity: [0, 1, 0.8, 1, 0]
        };
      case 'water':
        return {
          y: baseY,
          x: [0, Math.sin(p.sinSeed) * 20, -Math.sin(p.sinSeed) * 20],
          opacity: [0, 0.8, 0.8, 0]
        };
      default:
        return {
          y: baseY,
          x: [0, p.baseX],
          opacity: [0, 1, 1, 0]
        };
    }
  };
  if (!isReady || particles.length === 0) return null;
  return <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`} data-id="element-229">
      {particles.map(particle => <motion.div key={particle.id} className="absolute rounded-full" style={{
      left: `${particle.x}%`,
      bottom: 0,
      width: `${particle.size}px`,
      height: `${particle.size}px`,
      willChange: 'transform, opacity',
      ...getParticleStyle(type)
    }} animate={getAnimation(type, particle)} transition={{
      duration: particle.duration,
      delay: particle.delay,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-230" />)}
    </div>;
}